/**
 * Environment identity — which environment this build *is*, as opposed to which
 * config tier it gets.
 *
 * `__CIO_ENV__` decides two unrelated things at once:
 *
 * 1. **Config tier** — which Sentry DSN, recaptcha key, Segment key, Stripe
 *    publishable key, Fullstory gate and dev-helper affordances a build gets.
 * 2. **Environment identity** — which API host to call, and which OAuth client
 *    is ours.
 *
 * Those were the same thing until an isolated environment (preprod-dodo) needed
 * the `standalone` *tier* while not being staging. Giving it its own
 * `__CIO_ENV__` value would have re-decided every tier branch at the same time,
 * trading four known leaks for a dozen unknown ones — so identity moved out
 * here instead, supplied at build time, and `__CIO_ENV__` keeps meaning tier.
 *
 * **Every host or OAuth-client resolver must consult this module**, and there are
 * two ways to do so. Read the identity directly, as the API base URLs and the OIDC
 * client do; or resolve through `getCurrentEnvironment()`, which honours the identity
 * on the caller's behalf and so covers everything keyed on `Environment` without
 * each resolver knowing this module exists.
 *
 * The failure mode is silent and expensive: without that second path,
 * `getCurrentEnvironment()` classifies an isolated environment's unrecognised host
 * as PRODUCTION, and a resolver keyed on it hands out a credentialed production
 * websocket, live billing ids or the production MCP host rather than failing. All
 * four leaks found so far were found by a review bot or a failed sign-in, never by
 * reading the code, which is why `environment-identity.test.ts` fails the build when
 * a new `__CIO_ENV__` branch appears in a resolver that does not consult this.
 *
 * Deliberately NOT keyed off `VITE_API_URL`: that is already set for production,
 * standalone and the external-UI build, so keying off it would repoint those.
 */

/** An environment that supplies its own identity, e.g. `preprod-dodo`. */
export type EnvironmentIdentity = {
  /**
   * The single host this environment serves everything from, with no region
   * split and no un-prefixed sibling, e.g. `https://fly-preprod-dodo.customer.io`.
   */
  apiHost: string;
  /**
   * This environment's own Google OAuth client id. Client ids are public; the
   * matching secret is server-side. Supplied at build time rather than listed in
   * app code so standing up an environment needs no code change.
   */
  googleOidcClientId: string;
  /**
   * This environment's Design Studio language-server websocket host, e.g.
   * `wss://ds-languages-preprod-dodo.customer.io`. It is a separate subdomain, so
   * it cannot be derived from {@link EnvironmentIdentity.apiHost}.
   */
  dsLanguagesHost: string;
};

function readBuildTimeValue(value: string | undefined): string {
  // Trailing slashes are stripped because callers concatenate paths onto the host
  // (`${apiHost}/cdp/api`, `${apiHost}/design-studio/procedures`), and a deploy
  // value with one produces `//cdp/api`.
  return (value ?? '').trim().replace(/\/+$/, '');
}

/**
 * The identity of the environment this bundle was built for, or `null` for the
 * ordinary environments whose identity is still implied by `__CIO_ENV__`
 * (production, standalone, development).
 *
 * Individual fields can be empty even when identity is set — an environment may
 * override its host without owning a Google client, or the reverse — so callers
 * check the field they need rather than treating this as all-or-nothing.
 */
export function getEnvironmentIdentity(): EnvironmentIdentity | null {
  const apiHost = readBuildTimeValue(import.meta.env.VITE_API_HOST_OVERRIDE);
  const googleOidcClientId = readBuildTimeValue(import.meta.env.VITE_GOOGLE_OIDC_CLIENT_ID);
  const dsLanguagesHost = readBuildTimeValue(import.meta.env.VITE_DS_LANGUAGES_HOST_OVERRIDE);

  if (apiHost === '' && googleOidcClientId === '' && dsLanguagesHost === '') {
    return null;
  }

  return { apiHost, googleOidcClientId, dsLanguagesHost };
}

/**
 * This environment's own host, or `''` when identity is implied by
 * `__CIO_ENV__`. An empty return means "fall through to the environment maps".
 */
export function getApiHostOverride(): string {
  return getEnvironmentIdentity()?.apiHost ?? '';
}

/**
 * This environment's own Google OAuth client id, or `''` to use the one its
 * `__CIO_ENV__` tier implies.
 */
export function getGoogleOidcClientIdOverride(): string {
  return getEnvironmentIdentity()?.googleOidcClientId ?? '';
}

/**
 * This environment's own Design Studio language-server websocket host, or `''`
 * when it supplies none. An isolated environment (see {@link getApiHostOverride})
 * that leaves this empty must get `''` from its resolver rather than a staging or
 * production host, so the missing value fails visibly.
 */
export function getDesignStudioLanguagesHostOverride(): string {
  return getEnvironmentIdentity()?.dsLanguagesHost ?? '';
}
